<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
    GripVertical, Server, Hash, Sun, Languages, Eye, Wifi, Download, AlertTriangle, Monitor
} from 'lucide-vue-next'
import BBtn from '@lib/components/base/BBtn.vue'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import { useClipboardStore, type ClipboardService } from '@/stores/clipboard'
import { renderServiceCode, type ServiceVariant } from '@/lib/serviceCode'
import { buildDeepLink, isDeepLinkable } from '@/lib/deepLink'

const props = defineProps<{ code: ClipboardService }>()
defineEmits<{ (e: 'remove', id: number): void }>()

const { t } = useI18n()
const clipboard = useClipboardStore()

const qrPayload = computed(() => {
    const raw = renderServiceCode(props.code.service)
    // When the "URL in QR" toggle is off, emit the bare payload (no deep-link).
    if (!clipboard.urlInQr) return raw
    return isDeepLinkable(props.code) ? buildDeepLink(raw) : raw
})

const HW_ONLY_KINDS: ServiceVariant['kind'][] = [
    'server', 'display', 'wifi', 'start-update', 'reset'
]
const isHwOnly = computed(() => HW_ONLY_KINDS.includes(props.code.service.kind))

const labelFor = computed(() => {
    const s = props.code.service
    switch (s.kind) {
        case 'server':       return t(`clipboard.service.server.${s.variant}`)
        case 'counter':      return s.variant === 1
            ? t('clipboard.service.counter.on')
            : t('clipboard.service.counter.off')
        case 'display':      return t('clipboard.service.display.label', { pct: s.variant })
        case 'language':     return t(`clipboard.service.language.${s.variant}`)
        case 'scan-info':    return t(`clipboard.service.scanInfo.${s.variant.toLowerCase()}`)
        case 'wifi':         return t('clipboard.service.wifi.label', { ssid: s.variant.ssid })
        case 'start-update': return t('clipboard.service.startUpdate.label')
        case 'reset':        return t('clipboard.service.reset.label')
    }
})

const subtitleFor = computed(() => {
    const s = props.code.service
    switch (s.kind) {
        case 'server':       return t('clipboard.service.server.subtitle')
        case 'counter':      return t('clipboard.service.counter.subtitle')
        case 'display':      return t('clipboard.service.display.subtitle')
        case 'language':     return t('clipboard.service.language.subtitle')
        case 'scan-info':    return t('clipboard.service.scanInfo.subtitle')
        case 'wifi':         return t('clipboard.service.wifi.subtitle')
        case 'start-update': return t('clipboard.service.startUpdate.subtitle')
        case 'reset':        return t('clipboard.service.reset.subtitle')
    }
})

const CategoryIcon = computed(() => {
    switch (props.code.service.kind) {
        case 'server':       return Server
        case 'counter':      return Hash
        case 'display':      return Sun
        case 'language':     return Languages
        case 'scan-info':    return Eye
        case 'wifi':         return Wifi
        case 'start-update': return Download
        case 'reset':        return AlertTriangle
    }
})
</script>

<template>
    <!--
        Service card. Záměrně odlišný layout od ClipboardCard.vue:
          • žádný HTML5 draggable na rootu → nelze ho použít jako source merge gestu
          • žádný drop overlay → merge sem nikdo nedostane
          • žádné tlačítko Rozpojit (canSplit by byl vždy false)
        Sortable reorder zůstává — pomocí stejné CSS třídy .clipboard-card-handle
        jako tikectové karty.
    -->
    <div
        class="border border-gray-200 rounded-12 p-3 flex items-stretch gap-3 overflow-hidden relative bg-gray-50"
        :data-id="code.id"
    >
        <div
            class="clipboard-card-handle absolute top-1 left-1 p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            :title="t('clipboard.dragHandle')"
        >
            <GripVertical class="w-4 h-4" />
        </div>

        <div
            v-if="isHwOnly"
            data-test="service-hw-badge"
            class="absolute top-1 right-1 p-1 text-gray-400 print:hidden"
            :title="t('clipboard.service.hwOnlyCard')"
        >
            <Monitor class="w-4 h-4" />
        </div>

        <div class="flex-1 flex flex-col items-center gap-2 min-w-0 w-full">
            <QrPreview :code="qrPayload" label="" :size="160" />

            <div class="text-sm font-semibold inline-flex items-center gap-1 text-center break-words">
                <component :is="CategoryIcon" class="w-3.5 h-3.5 shrink-0 text-ink/70" />
                <span>{{ labelFor }}</span>
            </div>

            <div class="text-xs text-gray-500 print:hidden text-center break-words">
                {{ subtitleFor }}
            </div>

            <div class="mt-auto flex items-center gap-1 flex-wrap justify-center">
                <BBtn
                    severity="secondary"
                    size="small"
                    data-test="service-remove"
                    @click="$emit('remove', code.id)"
                >
                    {{ t('clipboard.remove') }}
                </BBtn>
            </div>
        </div>
    </div>
</template>
